import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search,
  Download,
  RefreshCw,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Leaf,
  Mail,
  Globe,
  Loader2,
  KeyRound,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { api, API_BASE } from '@/lib/api';
import { ADMIN } from '@/constants/testIds';
import { toast } from 'sonner';
import ChangePasswordDialog from '@/components/site/ChangePasswordDialog';

const PAGE_SIZE = 20;

function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(iso || '');
  }
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: PAGE_SIZE });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  // Detect "must change password" flag set on login
  useEffect(() => {
    try {
      if (sessionStorage.getItem('klymart_must_change_password') === '1') {
        setMustChangePassword(true);
        setShowChangePassword(true);
      }
    } catch (err) {
      // sessionStorage may be unavailable (private browsing). Fail open — the user can
      // still change their password via the header button on the dashboard.
      if (process.env.NODE_ENV === 'development') {
        console.debug('[admin] sessionStorage unavailable, must-change auto-open skipped:', err?.message);
      }
    }
  }, []);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));
  }, [data.total]);

  const fetchData = useCallback(
    async (signal) => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get('/admin/signups', {
          params: { q: debouncedSearch || undefined, page, page_size: PAGE_SIZE },
          signal,
        });
        setData(res.data);
      } catch (err) {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          // Cookie missing/expired — backend already considers us logged out.
          navigate('/admin/login');
          return;
        }
        const msg = err?.response?.data?.detail || 'Failed to load signups';
        setError(typeof msg === 'string' ? msg : 'Failed to load signups');
      } finally {
        setLoading(false);
      }
    },
    // PAGE_SIZE and `api` are module-level / stable singletons.
    [debouncedSearch, page, navigate],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    fetchData(ctrl.signal);
    return () => ctrl.abort();
  }, [fetchData]);

  const onSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const onLogout = async () => {
    try {
      await api.post('/admin/logout');
    } catch (err) {
      // Logout is best-effort — if the server is unreachable, we still
      // navigate to the login screen so the admin sees the right UI.
      if (process.env.NODE_ENV === 'development') {
        console.debug('[admin] logout request failed:', err?.message);
      }
    }
    navigate('/admin/login');
  };

  const onExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('q', debouncedSearch);
      const qs = params.toString();
      const url = `${API_BASE}/admin/signups/export${qs ? `?${qs}` : ''}`;
      // Same-origin GET — the HttpOnly admin cookie is automatically sent
      // by the browser (SameSite=Lax allows top-level navigations).
      const a = document.createElement('a');
      a.href = url;
      a.rel = 'noopener';
      a.download = '';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('Export started');
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setTimeout(() => setExporting(false), 600);
    }
  };

  return (
    <div data-testid={ADMIN.shell} className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur supports-[backdrop-filter]:bg-background/70 border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2">
            <span className="h-8 w-8 rounded-lg border border-border bg-secondary flex items-center justify-center text-primary">
              <Leaf className="h-4 w-4" />
            </span>
            <div className="font-display text-lg tracking-tight">
              Klymart<sup className="text-[10px] ml-0.5 text-muted-foreground">TM</sup>
              <span className="text-muted-foreground font-sans text-sm"> · Admin</span>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => fetchData()}
              data-testid={ADMIN.refresh}
              className="h-9 rounded-lg bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-border"
            >
              <RefreshCw className="h-4 w-4 mr-1.5" />
              Refresh
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowChangePassword(true)}
              data-testid="admin-change-password-button"
              className="h-9 rounded-lg text-muted-foreground hover:text-foreground"
            >
              <KeyRound className="h-4 w-4 mr-1.5" />
              Change password
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              data-testid={ADMIN.logout}
              className="h-9 rounded-lg text-muted-foreground hover:text-foreground"
            >
              <LogOut className="h-4 w-4 mr-1.5" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
        >
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                Waitlist signups
              </div>
              <h1 className="font-display mt-1 text-3xl tracking-[-0.01em]">
                {data.total.toLocaleString()} brand{data.total === 1 ? '' : 's'}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={onSearchChange}
                  data-testid={ADMIN.search}
                  placeholder="Search brand, email, website…"
                  className="h-10 pl-9 w-72 bg-secondary/50 border-border focus-visible:ring-ring"
                />
              </div>
              <Button
                onClick={onExport}
                disabled={exporting || data.total === 0}
                data-testid={ADMIN.export}
                variant="secondary"
                className="h-10 rounded-lg bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-border"
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-1.5" />
                )}
                Export CSV
              </Button>
            </div>
          </div>

          <Card className="mt-6 bg-card border border-border rounded-2xl overflow-hidden">
            <div className="relative">
              <Table data-testid={ADMIN.table}>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-[150px]">Created</TableHead>
                    <TableHead>Brand</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="hidden md:table-cell">Website</TableHead>
                    <TableHead className="hidden md:table-cell">Revenue range</TableHead>
                    <TableHead className="hidden lg:table-cell">Certifications</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && data.items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="py-16 text-center text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                        Loading…
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && data.items.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="py-16 text-center text-muted-foreground"
                        data-testid={ADMIN.empty}
                      >
                        {error ? (
                          <span className="text-destructive">{error}</span>
                        ) : (
                          'No signups yet. Once a brand joins the waitlist, it will appear here.'
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                  {data.items.map((row, idx) => (
                    <TableRow
                      key={row.id}
                      data-testid={`${ADMIN.rowPrefix}-${idx}`}
                      className="cursor-pointer hover:bg-secondary/40 transition-colors"
                      onClick={() => setDetail(row)}
                    >
                      <TableCell className="text-muted-foreground text-xs whitespace-nowrap">
                        {formatDate(row.created_at)}
                      </TableCell>
                      <TableCell className="font-medium">{row.brand_name}</TableCell>
                      <TableCell className="text-muted-foreground">{row.contact_name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        <span className="inline-flex items-center gap-1.5">
                          <Mail className="h-3.5 w-3.5" />
                          {row.email}
                        </span>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-muted-foreground">
                        {row.website ? (
                          <span className="inline-flex items-center gap-1.5">
                            <Globe className="h-3.5 w-3.5" />
                            {row.website}
                          </span>
                        ) : (
                          <span className="text-muted-foreground/60">—</span>
                        )}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-muted-foreground">
                        {row.brand_size || <span className="text-muted-foreground/60">—</span>}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {(row.certifications || []).slice(0, 3).map((c) => (
                            <Badge
                              key={c}
                              variant="secondary"
                              className="bg-secondary/70 border border-border text-xs font-normal"
                            >
                              {c}
                            </Badge>
                          ))}
                          {(row.certifications || []).length > 3 && (
                            <Badge variant="secondary" className="bg-secondary/70 border border-border text-xs font-normal">
                              +{(row.certifications || []).length - 3}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {/* Pagination */}
            {data.total > 0 && (
              <div
                data-testid={ADMIN.pagination}
                className="flex items-center justify-between gap-4 px-4 py-3 border-t border-border bg-secondary/20"
              >
                <div className="text-xs text-muted-foreground">
                  Page {data.page} of {totalPages} · Showing {data.items.length} of{' '}
                  {data.total.toLocaleString()}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    data-testid={ADMIN.prev}
                    className="h-8 rounded-lg"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Prev
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    data-testid={ADMIN.next}
                    className="h-8 rounded-lg"
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </motion.div>
      </main>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent
          data-testid={ADMIN.rowDetail}
          className="bg-card border border-border max-w-2xl"
        >
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">{detail.brand_name}</DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  Joined {formatDate(detail.created_at)}
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <DetailRow label="Contact" value={detail.contact_name} />
                <DetailRow label="Email" value={detail.email} />
                <DetailRow label="Website" value={detail.website || '—'} />
                <DetailRow label="Revenue range" value={detail.brand_size || '—'} />
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Certifications
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {(detail.certifications || []).length === 0 && (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                    {(detail.certifications || []).map((c) => (
                      <Badge
                        key={c}
                        variant="secondary"
                        className="bg-secondary border border-border text-xs font-normal"
                      >
                        {c}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Notes
                  </div>
                  <div className="mt-1.5 text-sm whitespace-pre-wrap text-foreground/90">
                    {detail.notes || '—'}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ChangePasswordDialog
        open={showChangePassword}
        mustChange={mustChangePassword}
        onOpenChange={(o) => {
          setShowChangePassword(o);
          if (!o) setMustChangePassword(false);
        }}
      />
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm text-foreground/90 break-words">{value}</div>
    </div>
  );
}
